// src/pages/AdminStudents.js
import { useState, useEffect } from 'react';
import { getAllStudents, updateStudent, deleteStudent } from '../../../services/adminService';
import './AdminStudents.css';

function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllStudents();
      setStudents(data);
    } catch (err) {
      setError('Error loading students: ' + (err.response?.data?.msg || err.message));
      console.error('Öğrenci yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteStudent(studentId);
      setStudents(prev => prev.filter(s => s._id !== studentId));
    } catch (err) {
      setError('Error deleting student: ' + (err.response?.data?.msg || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditStudent = async (studentId, updatedData) => {
    try {
      setActionLoading(true);
      const result = await updateStudent(studentId, updatedData);
      setStudents(prev => prev.map(s => 
        s._id === studentId ? { ...s, ...result.student } : s
      ));
      setEditingStudent(null);
    } catch (err) {
      setError('Error updating student: ' + (err.response?.data?.msg || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    // Search filter
    const matchesSearch = 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.phone && student.phone.includes(searchTerm));
    
    // Status filter
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && student.status !== 'inactive') ||
      (statusFilter === 'inactive' && student.status === 'inactive');
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: students.length,
    active: students.filter(s => s.status !== 'inactive').length,
    inactive: students.filter(s => s.status === 'inactive').length,
    totalCourses: students.reduce((acc, s) => acc + (s.courses?.length || 0), 0)
  };

  if (loading) {
    return (
      <div className="admin-students-loading">
        <div className="loading-spinner">👥</div>
        <p>Loading students...</p>
      </div>
    );
  }

  return (
    <div className="admin-students">
      {/* Header */}
      <div className="students-header">
        <div className="header-content">
          <h1>👥 Student Management</h1>
          <p>View and manage all students in the system</p>
        </div>
        <button 
          className="add-student-btn"
          onClick={() => alert('Add student modal will be added soon')}
          disabled={actionLoading}
        >
          {actionLoading ? '⏳' : '➕'} Add New Student
        </button>
      </div>

      {error && (
        <div className="students-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="students-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <div className="filter-group">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Statistics */}
      <div className="students-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Students</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active Students</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏸️</div>
          <div className="stat-info">
            <span className="stat-value">{stats.inactive}</span>
            <span className="stat-label">Inactive Students</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalCourses}</span>
            <span className="stat-label">Total Course Enrollments</span>
          </div>
        </div>
      </div>

      {/* Student List */}
      <div className="students-table-container">
        <table className="students-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Contact</th>
              <th>Enrolled Courses</th>
              <th>Registration Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-results">
                  {searchTerm ? 'No students match your search criteria.' : 'No students yet.'}
                </td>
              </tr>
            ) : (
              filteredStudents.map(student => (
                <tr key={student._id}>
                  <td>
                    <div className="student-info">
                      <div className="student-avatar">
                        {student.name.charAt(0)}
                      </div>
                      <div>
                        <div className="student-name">{student.name}</div>
                        <div className="student-id">ID: {student._id.slice(-6)}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <div>{student.email}</div>
                      {student.phone && <div>{student.phone}</div>}
                    </div>
                  </td>
                  <td>
                    <div className="courses-list">
                      {student.courses && student.courses.length > 0 ? (
                        student.courses.map((course, index) => (
                          <span key={index} className="course-tag">{course}</span>
                        ))
                      ) : (
                        <span className="no-courses">No courses</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {new Date(student.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td>
                    <span className={`status-badge ${student.status === 'inactive' ? 'inactive' : 'active'}`}>
                      {student.status === 'inactive' ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-edit"
                        onClick={() => setEditingStudent(student)}
                        disabled={actionLoading}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-view"
                        onClick={() => window.open(`/students/${student._id}`, '_blank')}
                        disabled={actionLoading}
                        title="View"
                      >
                        👁️
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteStudent(student._id)}
                        disabled={actionLoading}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal - Basic modal, to be improved later */}
      {editingStudent && (
        <div className="edit-modal">
          <div className="modal-content">
            <h3>Edit Student</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleEditStudent(editingStudent._id, {
                name: formData.get('name'),
                email: formData.get('email'),
                phone: formData.get('phone')
              });
            }}>
              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text" 
                  name="name" 
                  defaultValue={editingStudent.name}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input 
                  type="email" 
                  name="email" 
                  defaultValue={editingStudent.email}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input 
                  type="text" 
                  name="phone" 
                  defaultValue={editingStudent.phone || ''}
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-save" disabled={actionLoading}>
                  {actionLoading ? '⏳ Saving...' : '💾 Save'}
                </button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setEditingStudent(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStudents;